import React from "react";

import makeStyles from "@material-ui/core/styles/makeStyles";

import Card from "@material-ui/core/Card";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Divider from "@material-ui/core/Divider";
import MuiDrawer from "@material-ui/core/Drawer";

// icons
import IconButton from "@material-ui/core/IconButton";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

// perfect scrollbar
import PerfectScrollbar from "react-perfect-scrollbar";
import "react-perfect-scrollbar/dist/css/styles.css";

import Table from "jazasoft/lib/mui/components/Table";

import { getWeightedMean, isEmpty, toFixed, getDistinctValues } from "../../../../utils/helpers";
import { cbSortFlowInfo } from "../../../../helpers/orderHelper";
import { DEFAULT_SEPARATOR, UOM } from "../../../../constants";

import CardHeader from "../../../../components/CardHeader";
import useMutation from "../../../../hooks/useMutation";
import useSafeState from "../../../../hooks/useSafeState";
import useDeepEffect from "../../../../hooks/useDeepEffect";
import useDeepMemo from "../../../../hooks/useDeepMemo";

const columnsFabric = [
  { dataKey: "itemCode", title: "Item Code" },
  { dataKey: "width", title: "O. Width" },
  { dataKey: "actualWidth", title: "A. Width" },
  { dataKey: "reqFabric", title: "Req. Fab." },
  { dataKey: "allocFabric", title: "Alloc. Fab." },
  { dataKey: "avlFabric", title: "Avl. Fab." }
];

const columnsGroup = [
  { dataKey: "group", title: "Group" },
  { dataKey: "style", title: "Style" },
  { dataKey: "color", title: "Color" },
  { dataKey: "fabric", title: "Fabric" },
  { dataKey: "rollCount", title: "Roll Count" },
  { dataKey: "shades", title: "Shades" }
];

const useStyles = makeStyles(theme => ({
  drawer: ({ drawerWidth }) => ({
    width: drawerWidth,
    flexShrink: 0
  }),
  drawerPaper: ({ drawerWidth }) => ({
    width: drawerWidth
  }),
  drawerHeader: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1),
    // necessary for content to be below app bar
    ...theme.mixins.toolbar,
    justifyContent: "flex-start"
  },
  table: {
    whiteSpace: "pre"
  },
  search: {
    margin: theme.spacing(1),
    width: "100%"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center",
    margin: "1.5em"
  },
  mrTop: {
    marginTop: "1em"
  }
}));

const Drawer = props => {
  const { open, order, flowInfoList, orderFlowList, onDrawerClose, drawerWidth = 500 } = props;

  const [fabricMap, setFabricMap] = useSafeState({}); // key: fabricId, value: Fabric Object
  const [groupsMap, setGroupsMap] = useSafeState({}); // key: orderFlowId, value: GroupList
  const [tabIdx, setTabIdx] = useSafeState(0);

  const [mutate] = useMutation();

  useDeepEffect(() => {
    if (!open) return;

    const fabricIds =
      flowInfoList
        ?.map(fi => fi.ledgerList[0]?.fabricId)
        .filter(id => !isEmpty(id))
        .filter(id => !Object.keys(fabricMap).includes(`${id}`)) || [];
    const flowIds = orderFlowList?.map(of => of.id).filter(id => !Object.keys(groupsMap).includes(`${id}`)) || [];

    if (!isEmpty(fabricIds)) {
      fabricIds.forEach(fabricId => {
        const options = {
          url: `fabrics/${fabricId}`,
          method: "get"
        };
        mutate(options, {
          onSuccess: fabric => setFabricMap(prev => ({ ...prev, [fabricId]: fabric }))
        });
      });
    }
    if (!isEmpty(flowIds)) {
      flowIds.forEach(flowId => {
        const options = {
          url: `orders/${order?.id}/groups`,
          method: "get",
          params: { orderFlowId: flowId }
        };
        mutate(options, {
          onSuccess: groupList => setGroupsMap(prev => ({ ...prev, [flowId]: groupList }))
        });
      });
    }
  }, [open, order?.id, flowInfoList, orderFlowList]);

  // Hooks
  const classes = useStyles({ drawerWidth });

  const rowsFabric = useDeepMemo(() => {
    const flowList = orderFlowList?.sort((a, b) => b.lot - a.lot) || [];
    const flow = flowList[tabIdx];

    const fiList = flowInfoList
      ?.filter(fi => flow.styleColorList?.includes(fi.styleColor) && flow.productIdList?.includes(fi.product?.id) && flow.part === fi.part)
      .sort((a, b) => cbSortFlowInfo(a, b))
      .map(fi => ({ ...fi, fabricId: fi.ledgerList[0]?.fabricId }))
      .map(fi => ({ ...fi, fabric: fabricMap[fi.fabricId] }));

    const rows = fiList.map(fi => {
      const fabric = fi.fabric || {};

      const actualWidth = getWeightedMean(fabric.rollInfoList?.map(ri => ({ key: ri.width, weight: ri.length })));
      const sbList = order?.fpoSizeBreakupList?.filter(sb => sb.style === fi.style && sb.color === fi.color && sb.productId === fi.product?.id);
      const orderQty = sbList?.reduce((acc, sb) => acc + sb.qty, 0) || 0;

      const unit = fi.uom === UOM.GRAM.value ? "kg" : "m";
      const ledgerIds = fi.ledgerList?.filter(l => flow.lot > l.lot).map(l => l.id);
      const ledgerId = fi.ledgerList?.find(l => flow.lot === l.lot)?.id;

      const prevAllocRolls = fabric?.rollInfoList?.filter(ri => ledgerIds.includes(ri.ledgerOutId)) || [];
      const allocRolls = fabric?.rollInfoList?.filter(ri => ledgerId === ri.ledgerOutId) || [];
      const balanceRollList = fabric?.rollInfoList?.filter(ri => isEmpty(ri.ledgerOutId)) || [];

      const prevAllocLength = prevAllocRolls.reduce((acc, ri) => acc + ri.length, 0);
      const prevAllocWeight = prevAllocRolls.reduce((acc, ri) => acc + (ri.weight || 0), 0);
      const allocLength = allocRolls.reduce((acc, ri) => acc + ri.length, 0);
      const allocWeight = allocRolls.reduce((acc, ri) => acc + (ri.weight || 0), 0);
      const avlLength = balanceRollList.reduce((acc, ri) => acc + ri.length, 0);
      const avlWeight = balanceRollList.reduce((acc, ri) => acc + (ri.weight || 0), 0);

      let reqFabric = 0;
      if (fi.uom === UOM.GRAM.value) {
        reqFabric = (fi.orderingCu || 0) * orderQty * 0.001;
        reqFabric = Math.max(0, reqFabric - prevAllocWeight);
      } else {
        reqFabric = (fi.orderingCu || 0) * orderQty;
        reqFabric = Math.max(0, reqFabric - prevAllocLength);
      }

      return {
        itemCode: fabric.itemCode,
        width: `${toFixed(fabric.width, 1)} cm`,
        actualWidth: `${toFixed(actualWidth, 1)} cm`,
        reqFabric: `${toFixed(reqFabric, 1)} ${unit}`,
        allocFabric: fi.uom === UOM.GRAM.value ? `${toFixed(allocWeight, 1)} kg` : `${toFixed(allocLength, 1)} m`,
        avlFabric: fi.uom === UOM.GRAM.value ? `${toFixed(avlWeight, 1)} kg` : `${toFixed(avlLength, 1)} m`
      };
    });

    return rows;
  }, [order, fabricMap, flowInfoList, orderFlowList, tabIdx]);

  const rowsGroup = useDeepMemo(() => {
    const flowList = orderFlowList?.sort((a, b) => b.lot - a.lot) || [];
    const flow = flowList[tabIdx];
    if (isEmpty(flow?.groupList)) return [];
    const hasFinalGroup = flow.groupList.some(g => g.type === "FINAL");

    const fiList = flowInfoList
      ?.filter(fi => flow.styleColorList?.includes(fi.styleColor) && flow.productIdList?.includes(fi.product?.id) && fi.part === flow.part)
      .map(fi => ({ ...fi, fabricId: fi.ledgerList[0]?.fabricId }));
    const ledgerIds = fiList?.flatMap(fi => fi.ledgerList?.map(l => l.id)) || [];
    const fabricList = fiList?.map(fi => fabricMap[fi.fabricId]).filter(f => !isEmpty(f)) || [];
    const rollInfoList = fabricList.flatMap(f => f.rollInfoList || []).filter(ri => ledgerIds.includes(ri.ledgerOutId));

    const rows = flow.groupList
      .filter(g => (hasFinalGroup ? g.type === "FINAL" : g.type === "SHRINKAGE"))
      .sort((a, b) => (a.name < b.name ? -1 : 1))
      .flatMap(group => {
        return flow.styleColorList.map(styleColor => {
          const riList = rollInfoList.filter(ri => ri.styleColor === styleColor && ri.groupId === group.id);

          const fabricLength = riList.reduce((acc, ri) => acc + ri.length, 0);
          const fabricWeight = riList.reduce((acc, ri) => acc + (ri.weight || 0), 0);

          const shadeList = getDistinctValues(riList.map(ri => ri.shade));
          const shades = shadeList
            .sort((a, b) => (a < b ? -1 : 1))
            .map(shade => {
              const total = rollInfoList.filter(ri => ri.styleColor === styleColor).reduce((acc, ri) => acc + ri.length, 0);
              const shadeQty = riList.filter(ri => ri.shade === shade).reduce((acc, ri) => acc + ri.length, 0);
              const percent = total > 0 ? toFixed((shadeQty * 100) / total, 1) : 0;
              return `${shade}(${percent}%)`;
            })
            .join(", ");

          return {
            group: group.name,
            style: styleColor.split(DEFAULT_SEPARATOR)[0],
            color: styleColor.split(DEFAULT_SEPARATOR)[1],
            fabric: fiList[0]?.uom === UOM.GRAM.value ? `${toFixed(fabricWeight, 1)} kg` : `${toFixed(fabricLength, 1)} m`,
            shades,
            rollCount: riList?.length
          };
        });
      });

    return rows;
  }, [orderFlowList, flowInfoList, fabricMap, tabIdx]);

  const tabList = orderFlowList?.sort((a, b) => b.lot - a.lot).map(e => `Lot ${e.lot}`) || [];

  return (
    <MuiDrawer
      className={classes.drawer}
      anchor="right"
      open={open}
      classes={{
        paper: classes.drawerPaper
      }}
      onClose={onDrawerClose}>
      <div className={classes.drawerHeader}>
        <IconButton onClick={onDrawerClose}>
          <ChevronRightIcon />
        </IconButton>
      </div>
      <Divider />

      <div>
        {tabList?.length > 1 && (
          <Tabs value={tabIdx} onChange={(e, tabIdx) => setTabIdx(tabIdx)} variant="scrollable" scrollButtons="auto">
            {tabList.map((tab, idx) => (
              <Tab key={idx} label={tab} />
            ))}
          </Tabs>
        )}
        {tabList?.length > 1 && <Divider />}
        <Card>
          <CardHeader title="Fabric Info" />
          <Divider />
          <PerfectScrollbar>
            <Table classes={{ table: classes.table }} columns={columnsFabric} rows={rowsFabric} />
          </PerfectScrollbar>
        </Card>
        <Card className={classes.mrTop}>
          <CardHeader title="Group Info" />
          <Divider />

          <PerfectScrollbar>
            <Table classes={{ table: classes.table }} columns={columnsGroup} rows={rowsGroup} emptyMessage="Grouping not done yet" />
          </PerfectScrollbar>
        </Card>
      </div>
    </MuiDrawer>
  );
};

export default Drawer;
